import React, { useMemo, useEffect, useState, useCallback } from 'react'
import Select from 'react-select'

const DcmWeekDaySelectUnit = ( { data, weekDayLabel, weekDayValue, onWeekDayToggle, ...props } ) => {
  const checked = useMemo( () => {
    return !!data?.worktime?.weekdays?.includes(weekDayValue)
  }, [ data, weekDayValue ] )

  const handleChange = useCallback( () => {
    if ( onWeekDayToggle ) onWeekDayToggle( weekDayValue )
  }, [ onWeekDayToggle, weekDayValue ] )

  return (
    <div {...props}>
      <div className='form-check'>
        <input className="form-check-input" type="checkbox" checked={checked} onChange={handleChange} />
        <label className="form-check-label font-weight-lighter">
          {weekDayLabel}
        </label>
      </div>
    </div>
  )
}

const DcmWeekDaysSelect = ( { data, setData, dataToSearchFinish } ) => {
  const [ timeInterval ] = useState( 10 )

  const setCardInRequired = useMemo( () => {
    if ( !data.worktime ) return false

    const areWeekDays = !!data.worktime.weekdays?.length
    const areStartAt = !!data.worktime.start_at
    const areStopAt = !!data.worktime.stop_at
    const areSomeThing = areWeekDays || areStartAt || areStopAt

    if ( areSomeThing ) return true

    return false
  }, [ data ] )

  const toSearchFinish = useMemo( () => {
    return dataToSearchFinish ?? data
  }, [ dataToSearchFinish, data ] )

  const finish = useMemo( () => {
    return toSearchFinish.finish
      ? Array.isArray( toSearchFinish.finish )
        ? toSearchFinish.finish[0]
        : toSearchFinish.finish
      : toSearchFinish.momentums?.find( ( { type } ) => type === 'finish' )?.values?.[0]
      ?? {}
  }, [ toSearchFinish ] )

  useEffect( () => {
    if ( !data.worktime ) return void 0

    const areWeekDays = !!data.worktime.weekdays?.length
    const areStartAt = !!data.worktime.start_at
    const areStopAt = !!data.worktime.stop_at
    const areSomeThing = areWeekDays || areStartAt || areStopAt

    if ( !areSomeThing ) return void 0
    if ( data.worktime.end_date === finish.value ) return void 0

    setData( data => ( {
      ...data,
      worktime: {
        ...data.worktime,
        end_date: finish.value
      }
    } ) )
  }, [ finish, data.worktime, setData ] )

  const handleWeekDayToggle = useCallback( ( weekDayValue ) => {
    setData( data => {
      const weekdaysNormalizedArray = data?.worktime?.weekdays ?? []
      const checked = weekdaysNormalizedArray.includes( weekDayValue )
      const weekdays = (
        checked
        ? weekdaysNormalizedArray.filter(weekday => weekday !== weekDayValue)
        : weekdaysNormalizedArray.concat(weekDayValue)
      ).sort( ( a, b ) => {
        if ( a > b ) return 1
        if ( b > a ) return -1
        return 0
      } )
      const worktime = { ...data.worktime, weekdays }
      return { ...data, worktime }
    } )
  }, [ setData ] )

  const handleHourStart = useCallback( ( event ) => {
    setData( data => {
      const start_at = event?.value
      const worktime = { ...data?.worktime, start_at }

      return { ...data, worktime }
    } )
  }, [ setData ] )

  const handleHourEnd = useCallback( ( event ) => {
    setData( data => {
      const stop_at = event?.value
      const worktime = { ...data?.worktime, stop_at }

      return { ...data, worktime }
    } )
  }, [ setData ] )

  const hoursOptions = useMemo( () => {
    const options = Array( 24 * 60 / timeInterval ).fill( 0 ).map( ( v, index ) => {
      const calc = index * timeInterval / 60
      const hours = Math.trunc( calc ).toString().padStart( 2, '0' )
      const minutes = ( 60 * ( calc % 1 ) ).toFixed( 0 ).padStart( 2, '0' )
      return {
        label: `${hours}:${minutes}`,
        value: `${hours}:${minutes}`
      }
    } )
    return options
  }, [ timeInterval ] )

  return (
    <div className="block block__week_days">
      <div className="block__body">
        <p className="title" style={ {
          background: 'linear-gradient( 135deg, #ced4da 0%, #ced4da 49.9%, #eea849 50%, #f46b45 100% )',
          backgroundSize: '250%',
          backgroundPosition: '100%, 0',
          color: '#fff',
        } }>
          <i className="linearicons-calendar-full"></i> Cronograma
          { setCardInRequired && <span className="title__addon">Obrigatório</span> }
        </p>
        <div className="conditions__wrapper">
          <div className='row'>
            <div className='col-6'>
              <div className='row no-gutters'>
                {[ 'Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sabado' ]
                  .map((weekDayLabel, weekDayValue) => {
                    return <DcmWeekDaySelectUnit
                      className='col-4'
                      key={`week-day.${weekDayValue}`}
                      data={data}
                      weekDayLabel={weekDayLabel}
                      weekDayValue={weekDayValue}
                      onWeekDayToggle={handleWeekDayToggle}
                    />
                  })}
              </div>
            </div>
            <div className='col-6'>
              <div className='d-flex flex-row align-items-center'>
                <label className='m-0 font-weight-lighter' style={ { flex: 0.4 } }>Hora início:</label>
                <Select isClearable
                  options={hoursOptions}
                  onChange={handleHourStart}
                  placeholder='Selecione o horário inicial'
                  styles={ {
                    container: base => ( { ...base, flex: 1 } ),
                    menu: base => ( { ...base, zIndex: 999999 } )
                  } }
                />
              </div>
              <div className='d-flex flex-row align-items-center'>
                <label className='m-0 font-weight-lighter' style={ { flex: 0.4 } }>Hora fim:</label>
                <Select isClearable
                  options={hoursOptions}
                  onChange={handleHourEnd}
                  placeholder='Selecione o horário final'
                  styles={ {
                    container: base => ( { ...base, flex: 1 } ),
                    menu: base => ( { ...base, zIndex: 999999 } )
                  } }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { DcmWeekDaysSelect }
